import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, Lock, ShieldCheck, Download, MessageCircleHeart, PlayCircle, ClipboardList, Globe2 } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { CoursePlayer } from "@/components/CoursePlayer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { api, API, errMsg, startCheckout } from "@/lib/api";

const AUDIENCES = [
  ["The exhausted bedside nurse", "You need a way out of the physical and emotional strain. We help you pick a role that genuinely reduces it, rather than swapping one kind of exhaustion for another."],
  ["The nurse who wants to work from home", "You don't know where to start. Modules 1, 2 and 4 map your experience to specific roles and show you exactly what to search for."],
  ["The experienced nurse who keeps getting rejected", "It's often not your experience. It's the wording, the format and the screening software. Modules 7, 8 and 10 fix that."],
  ["The nurse who thinks they need more qualifications", "Many remote roles need no extra certification, and some credentials can't be taken until you're in the role. Module 9 shows the right order."],
  ["The newly graduated nurse", "Honestly: most remote roles ask for about two years of bedside experience. We show the realistic path and which roles to target early."],
];
const INCLUDED = [
  [PlayCircle, "12 short AI-presenter videos", "A welcome video plus one per module, with captions and transcripts."],
  [ClipboardList, "11 modules with exercises", "Lesson text, a saved exercise in every module, and progress tracking."],
  [Download, "Downloads you'll actually use", "Resume template (Word), keyword table, certification table, application tracker and 90-day checklist."],
  [MessageCircleHeart, "AI Career Coach", "Ask questions, rewrite resume bullets, check postings for scams and run mock interviews. Members only."],
  [Globe2, "US-primary with UK notes", "Each relevant module includes a UK section: NHS, NMC, CVs and supporting statements."],
  [ShieldCheck, "Honest, sourced guidance", "No income promises, no job guarantees. Approximate figures are labelled and point to official sources."],
];
const FAQ = [
  ["Will this course get me a remote job?", "No course can promise that, and we don't. It teaches you how to target the right roles, find real postings, pass screening software and prepare for interviews. Outcomes depend on your experience, licence, market conditions and effort."],
  ["How much experience do I need?", "Most remote roles ask for about two years of bedside experience; utilization review often asks for two to five years of hospital experience. New graduates will find a realistic path in Modules 2 and 11."],
  ["Is it for US or UK nurses?", "It's US-primary, with dedicated UK notes in each relevant module (NHS Jobs, NMC, CVs and supporting statements)."],
  ["Do I need a certification before I start?", "Usually not. Module 9 explains which credentials help, which need role experience first, and the order to pursue them in. Always verify fees and eligibility with the certifying body."],
  ["Who presents the videos?", "An AI-generated presenter, the \"Remote Nursing Career Coach\". It is not a real nurse or a real person. The AI Career Coach chat is also an AI assistant."],
  ["What are the promo options?", "The course was $250. Until 31/12/2026 you can choose: $197 with full access after 3 days, $97 with full access after 1 week, or $57 with full access after 2 weeks. You create your account straight after payment, and we email you the moment your course opens."],
  ["How do I sign in, and can I share my account?", "After payment you create your account with your purchase email and a password. Access is personal: it only works with that email, and signing in on a new device requires a verification code sent to that email. Accounts can't be shared."],
  ["Can I get a refund?", "No. All sales are final because full access starts immediately after payment. Please watch the welcome video and read the outline and FAQ first. Duplicate or accidental charges are always corrected."],
];

function FinalSale({ agreed, setAgreed, id }) {
  return (
    <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer text-left">
      <input id={id} type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-teal-600" data-testid={`${id}-checkbox`} />
      <span>I want immediate access to the course and understand that <strong>all sales are final (no refunds)</strong>.</span>
    </label>
  );
}

function PricingPanel({ pricing, onBuy, busyTier, agreed, setAgreed }) {
  const fmt = (n) => `$${Number(n).toFixed(0)}`;
  return (
    <motion.div id="buy-panel" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }} className="rounded-2xl bg-white border border-teal-200 p-5 sm:p-6 shadow-lg shadow-teal-900/5 space-y-4" data-testid="buy-course-panel">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="font-display text-xl font-bold text-slate-900">Ready to plan your move?</p>
          <p className="text-sm text-slate-600">11 modules · videos · downloads · AI Career Coach</p>
        </div>
        {pricing.promo_active && (
          <p className="text-sm" data-testid="was-price"><span className="text-slate-500">Was </span><span className="line-through text-slate-500 font-semibold">{fmt(pricing.was_price)}</span> <span className="ml-1 text-xs font-bold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">Promo now on</span></p>
        )}
      </div>
      <div className="grid gap-3">
        {pricing.tiers.map((t, i) => (
          <Button key={t.id} size="lg" onClick={() => onBuy(t.id)} disabled={!!busyTier} className={`rounded-2xl h-auto min-h-14 py-3 px-5 text-base whitespace-normal text-left justify-between gap-3 ${i === 0 ? "bg-teal-600 hover:bg-teal-700" : "bg-white text-teal-900 border-2 border-teal-600 hover:bg-teal-50"}`} data-testid={`buy-tier-${t.id}`}>
            <span>{t.label}</span>
            {busyTier === t.id ? <Loader2 className="h-5 w-5 animate-spin shrink-0" /> : <ArrowRight className="h-5 w-5 shrink-0" />}
          </Button>
        ))}
      </div>
      <FinalSale agreed={agreed} setAgreed={setAgreed} id="final-sale" />
      {pricing.promo_active && <p className="text-sm font-semibold text-rose-700" data-testid="promo-ends">Promotion ends {pricing.promo_ends}</p>}
    </motion.div>
  );
}

export default function Landing() {
  const [course, setCourse] = useState(null);
  const [ended, setEnded] = useState(false);
  const [busy, setBusy] = useState("");
  const [agreed, setAgreed] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get("/public/course").then((r) => setCourse(r.data)).catch(() => toast.error("Could not load course details")); }, []);

  const buy = async (tier) => {
    if (!agreed) { toast.error("Please confirm you understand that all sales are final."); document.getElementById("final-sale")?.focus(); return; }
    setBusy(tier);
    try { const to = await startCheckout(tier); if (to) nav(to); } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const skip = () => { setEnded(true); setTimeout(() => document.getElementById("buy-panel")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); };

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,#ccfbf1_0%,transparent_55%),radial-gradient(ellipse_at_bottom_right,#e0f2fe_0%,transparent_50%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-10 sm:pt-16 pb-16 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-start">
            <div className="space-y-6 rise lg:pt-8">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-800 bg-teal-50 border border-teal-100 px-3 py-1 rounded-full inline-block">The nurse's transition course</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900" data-testid="landing-headline">
                From bedside to remote, <span className="text-teal-700">with a realistic plan.</span>
              </h1>
              <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                Remote nursing is many different jobs, not one. Learn which roles your experience fits, how to find real postings, get past screening software and scams, and follow a dated 90-day plan.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                {["Honest about metrics, pay and job stability", "No income promises or job guarantees", "Watch the welcome video to see if it's right for you"].map((t) => (
                  <li key={t} className="flex gap-2"><Check className="h-5 w-5 text-teal-600 shrink-0" aria-hidden />{t}</li>
                ))}
              </ul>
            </div>
            <div className="space-y-5 rise" style={{ animationDelay: "120ms" }}>
              {course ? (
                <CoursePlayer video={course.welcome} src={`${API}/media/welcome/video`} captionsSrc={`${API}/media/welcome/captions`} transcript={course.welcome.transcript} title="Welcome video" onEnded={() => setEnded(true)} />
              ) : <div className="aspect-video rounded-2xl bg-slate-200 animate-pulse" />}
              <AnimatePresence>{ended && course && <PricingPanel pricing={course.pricing} onBuy={buy} busyTier={busy} agreed={agreed} setAgreed={setAgreed} />}</AnimatePresence>
            </div>
          </div>
        </section>

        <section id="modules" className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
            <div className="lg:sticky lg:top-24 self-start space-y-4">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">11 modules, in a sensible order</h2>
              <p className="text-slate-600">Work through them in order or dip in. Each has a short video, lesson text, an exercise you save in the course, and downloads where useful.</p>
            </div>
            <ol className="space-y-3" data-testid="module-outline">
              {(course?.modules || []).map((m) => (
                <li key={m.slug} className="group bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] flex gap-5" data-testid={`outline-module-${m.number}`}>
                  <span className="font-display text-3xl font-extrabold text-teal-600/80 w-10 shrink-0 tabular-nums">{String(m.number).padStart(2, "0")}</span>
                  <div className="space-y-1.5">
                    <h3 className="text-lg font-bold text-slate-900">{m.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{m.summary}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="h-3 w-3" aria-hidden /> Members · ~{m.duration_min} min video</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="who" className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 space-y-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">Who it's for</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {AUDIENCES.map(([t, d], i) => (
                <div key={t} className={`rounded-2xl p-6 border ${i === 4 ? "bg-sky-50 border-sky-100" : "bg-slate-50 border-slate-200"}`} data-testid={`audience-${i}`}>
                  <h3 className="font-bold text-slate-900 mb-2">{t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
                </div>
              ))}
              <div className="rounded-2xl p-6 border border-amber-200 bg-amber-50/60">
                <h3 className="font-bold text-amber-950 mb-2">Not for you if…</h3>
                <p className="text-sm text-amber-900 leading-relaxed">you want a guaranteed job, a quick income jump, or a role with no productivity targets. Remote roles are metrics-driven and teams can be restructured.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="included" className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-[1fr_0.9fr] gap-12 items-center">
          <div className="space-y-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">What's included</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {INCLUDED.map(([Icon, t, d]) => (
                <div key={t} className="space-y-2">
                  <Icon className="h-6 w-6 text-teal-700" aria-hidden />
                  <h3 className="font-bold text-slate-900">{t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
          <img src="https://images.unsplash.com/photo-1758691462743-f9fc9e430d39?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="A nurse working at a home computer setup with headset" className="rounded-3xl w-full aspect-[4/5] object-cover shadow-xl" loading="lazy" />
        </section>

        <section id="faq" className="bg-white border-y border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-24 space-y-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Questions, answered honestly</h2>
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {FAQ.map(([q, a], i) => (
                <AccordionItem key={q} value={`f${i}`}>
                  <AccordionTrigger className="text-left font-semibold" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section id="pricing" className="max-w-3xl mx-auto px-4 sm:px-8 py-16 text-center space-y-4">
          <p className="font-display text-2xl font-bold text-slate-900">{course?.pricing?.promo_active ? <>Promo pricing from $57. <span className="line-through text-slate-400">Was $250</span></> : "One-time payment."} Secure checkout by Stripe.</p>
          {course?.pricing?.promo_active && <p className="text-sm font-semibold text-rose-700">Promotion ends {course.pricing.promo_ends}</p>}
          <p className="text-sm text-slate-600">
            Watch the welcome video above first, or{" "}
            <button onClick={skip} className="text-teal-800 underline underline-offset-4 hover:text-teal-900" data-testid="skip-to-purchase-link">skip to purchase</button>.
          </p>
          <p className="text-xs text-slate-500">
            All sales are final. By purchasing you agree to the <Link to="/legal/terms" className="underline" data-testid="pricing-terms-link">Terms</Link> and <Link to="/legal/refund" className="underline" data-testid="pricing-refund-link">No-refund policy</Link>. Already bought it? <Link to="/login" className="underline" data-testid="pricing-restore-link">Restore access</Link>.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
